// Types shared between server and web.
//
// Kept intentionally narrow — only the shapes crossing the API boundary
// belong here. Server-internal helper types stay in server/, and view-model
// types stay in web/.

// ─── Auth ────────────────────────────────────────────────────────────

/** Derived role tier. Server maps raw squapi roles (+ an env-based PMM
 *  allowlist) → one of these:
 *   editor — EPD (eng, admin, product, design). Full edit access.
 *   pmm    — Product marketing. Can edit customer-facing copy (digest
 *            overrides, persona narratives) but not core entities.
 *   viewer — CS or SE. Read-only.
 *   sales  — Read-only + UI hides future-facing date fields.
 *   none   — Not a Squint team member (rejected by requireViewer). */
export type HubUserRole = "editor" | "pmm" | "viewer" | "sales" | "none";

/** View-lens preference. Orthogonal to auth — any signed-in user can pick
 *  any persona. Controls layout emphasis (which fields get promoted /
 *  demoted) but NOT auth gating (real sales users still can't see
 *  projected_ga, regardless of chosen persona).
 *
 *  `enablement` covers PMM + CS + Sales Engineering — the roles that
 *  enable others (customers, accounts, prospects) with launch info. They
 *  share needs: workspace enablement, gaps, and audience-flavored copy.
 *  Sales reps have distinct needs (talking points, no forward dates) so
 *  they stay a separate persona. */
export type Persona = "epd" | "enablement" | "sales";

export interface HubUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  roles: string[];
  isSquintTeamMember: boolean;
  hasEngRole: boolean;
  role: HubUserRole;
}

// ─── Squapi ──────────────────────────────────────────────────────────

export interface FeatureFlag {
  id: string;
  globalIsDisabled: boolean;
  platforms: string[];
  workspaces: string[];
  disallowedWorkspaces: string[];
  users: string[];
  description: string | null;
  defaultEnabled: boolean;
  minimumAppVersion: string | null;
  lastUpdated: string | null;
  timeCreated: string | null;
}

/** Onboarding classification for a workspace, mirroring squashboard's taxonomy
 *  (see squashboard/src/interfaces/classification.ts). Used to split
 *  customer-facing rollout signals from internal test workspaces:
 *    revenue           — external customers or prospects (what SEs care about)
 *    partner           — investors, press, partners
 *    internal          — internal Squint test workspaces
 *    internalImportant — internal (important) — still internal for UI purposes
 *    selfServe         — self-serve trials with no Salesforce opportunity yet
 *    null              — unclassified (treat as external-adjacent; surface but flag) */
export type WorkspaceClassification =
  | "revenue"
  | "partner"
  | "internal"
  | "internalImportant"
  | "selfServe";

export interface WorkspaceInfo {
  id: string;
  name: string;
  /** `null` when the workspace has no classification set in squapi. */
  classification: WorkspaceClassification | null;
  [k: string]: unknown;
}

// "in_development" = flag is on but reaches ZERO external (revenue)
// workspaces — internal/partner only. The tier below EA. See featureTier().
export type RolloutKind = "ga" | "ea" | "in_development" | "unreleased" | "disabled";

export interface RolloutState {
  kind: RolloutKind;
  label: string;
  workspace_count: number;
  ga_via: string | null;
}

/** Readiness axis — how finished a feature is. Independent of access
 *  (EA/GA), which is about *who* has it. Encoded as the `beta` boolean on
 *  FeatureEntry: beta=true ⇒ "Beta", beta=false ⇒ "Stable". This names both
 *  values so surfaces can say "Stable" as the explicit counterpart to Beta,
 *  rather than treating stable as merely the absence of a flag. */
export type Readiness = "beta" | "stable";

export function readinessOf(beta: boolean): Readiness {
  return beta ? "beta" : "stable";
}

export function readinessLabel(beta: boolean): string {
  return beta ? "Beta" : "Stable";
}

/** State the flag rollout implies for the feature's announcement. When
 *  `announcement_state` on the feature is null, this is the effective
 *  value — the editor is in "auto" mode. */
export function deriveAnnouncementStateFromRollout(
  rollout: RolloutState,
): "EA" | "GA" | "IN_DEV" | null {
  if (rollout.kind === "ga") return "GA";
  if (rollout.kind === "ea") return "EA";
  if (rollout.kind === "in_development") return "IN_DEV";
  return null;
}

/** Resolve the feature's effective announcement state:
 *    - stored override if the editor pinned one ("EA" or "GA")
 *    - otherwise fall back to what the flag rollout implies (which may be
 *      the derived-only "IN_DEV"/Unavailable tier) */
export function effectiveAnnouncementState(
  stored: "EA" | "GA" | null,
  rollout: RolloutState,
): "EA" | "GA" | "IN_DEV" | null {
  return stored ?? deriveAnnouncementStateFromRollout(rollout);
}

/** User-facing label for an announcement/access state. "IN_DEV" surfaces as
 *  "Unavailable" — the tier below EA (no external customer has it yet). */
export function announcementStateLabel(
  state: "EA" | "GA" | "IN_DEV" | null,
): string {
  if (state === "IN_DEV") return "Unavailable";
  return state ?? "";
}

// ─── Features ────────────────────────────────────────────────────────

export type PublishStatus = "draft" | "published";
export type FeatureCategory = "major" | "minor";
export type Surface = "web" | "ios" | "android";

export interface FeatureEntry {
  id: string;
  name: string;
  pitch: string | null;
  beta: boolean;
  flag_ids: string[];
  surface: Surface[];
  feature_family: string | null;
  known_gaps: string[];
  owner_email: string | null;
  /** Free-text "helpful links / notes" — supersedes the old structured
   *  prd_url / demo_url / linear_ticket_ids / github_pr_urls fields. Paste
   *  any links or context; rendered as a notes block on the card/detail. */
  links_notes: string | null;
  /** Uploaded images/video (same model as Release.media_urls). Ordered list
   *  of `/media/...` URLs; `media_captions` maps a URL → its caption. */
  media_urls: string[];
  /** Per-image caption, keyed by the media_urls entry it annotates. A URL
   *  absent from the map has no caption. */
  media_captions: Record<string, string>;
  first_seen_at: string | null;
  last_updated_at: string | null;
  status: PublishStatus;
  /** Editor's access override. null = "Auto — track the flag rollout".
   *  Only EA/GA can be pinned; the pre-EA "Unavailable" (IN_DEV) state is
   *  derived from the flag rollout, never set by hand. Resolve the effective
   *  value with effectiveAnnouncementState(). */
  announcement_state: "EA" | "GA" | null;
  /** Editor notes about platform support, shown beside the Web/Mobile
   *  toggles. Null/empty when not annotated. */
  web_note: string | null;
  mobile_note: string | null;
  /** EA transition date — concrete only (null | "TBD" | ISO date). A past
   *  date reads as *actual*, a future one as a *target*. */
  ea_date: string | null;
  /** The single GA target. Encoded like the old projected_ga (via
   *  lib/projectedGa.ts): null = unset, "TBD", "Q:Q1 2026" (quarter),
   *  "W:2026-W45" (ISO week), or an ISO date. A concrete date is a real GA
   *  milestone (past = actual, future = scheduled) and reaches the Timeline;
   *  a fuzzy value is a forward-looking projection and does not. */
  ga_date: string | null;
  announcement_narrative: string | null;
  /** Who this launch is for — one line PMM/Sales can consume without
   *  reverse-engineering the technical narrative. Ideally names a specific
   *  customer or persona. */
  audience: string | null;
  /** Editor provenance. Stamped by patchRow when any editable field changes;
   *  publish/unpublish also update this. */
  last_edited_by: string | null;
  last_edited_at: string | null;
  /** Set while a freshly-created draft is having its fields seeded from
   *  pasted notes by the background LLM pass; cleared when it finishes. The
   *  detail page shows a seeding skeleton (and blocks editing) while set. */
  seeding_at: string | null;
}

// ─── Analytics dashboard ─────────────────────────────────────────────

export interface AnalyticsEventCount {
  event: string;
  count: number;
}
export interface AnalyticsDayCount {
  day: string;
  count: number;
}
export interface AnalyticsEntityCount {
  entity_id: string;
  count: number;
}
export interface AnalyticsOrphanedDraft {
  id: string;
  name: string;
  owner_email: string | null;
  first_seen_at: string | null;
}
export interface AnalyticsRawEvent {
  id: string;
  event: string;
  actor_email: string | null;
  persona: string | null;
  path: string | null;
  entity_type: string | null;
  entity_id: string | null;
  meta: Record<string, unknown>;
  created_at: string | null;
}
export interface AnalyticsDashboard {
  days: number;
  since: string;
  by_event: AnalyticsEventCount[];
  by_day: AnalyticsDayCount[];
  top_features: AnalyticsEntityCount[];
  top_releases: AnalyticsEntityCount[];
  orphaned_drafts: AnalyticsOrphanedDraft[];
  recent_events: AnalyticsRawEvent[];
}

/** User-managed feature family. Referenced softly by
 *  `feature_entries.feature_family` (which is TEXT, not a FK). */
export interface FeatureFamily {
  id: string;
  name: string;
  created_at: string;
}

export interface EnrichedFeature extends FeatureEntry {
  rollout: RolloutState;
  flags: FeatureFlag[];
  missing_flags: string[];
}

export type ReleaseType = "EA" | "GA";

// ─── Releases ────────────────────────────────────────────────────────

export type ReleaseSurface = "web" | "mobile";
export type Platform = "ios" | "android" | null;

export interface GithubRelease {
  id: number;
  repo: string;
  surface: ReleaseSurface;
  platform: Platform;
  tag_name: string;
  name: string | null;
  body: string | null;
  author_login: string | null;
  html_url: string | null;
  published_at: string;
  raw: unknown;
  ingested_at: string;
  ignored: boolean;
}

/** Unified row for the Releases page — every github_release with its
 *  optional curated release attached. `release_id` null → un-converted. */
export interface UnifiedRelease {
  github_release_id: number;
  repo: string;
  surface: ReleaseSurface;
  platform: Platform;
  tag_name: string;
  github_name: string | null;
  github_body: string | null;
  html_url: string | null;
  author_login: string | null;
  github_published_at: string;
  ignored: boolean;
  release_id: string | null;
  status: PublishStatus | null;
  title: string | null;
  narrative: string | null;
  media_urls: string[] | null;
  release_date_override: string | null;
  published_at: string | null;
  item_count: number;
}

export interface StagingSummary {
  total_cached: number;
  unconverted: number;
  last_refreshed: string | null;
  by_repo: {
    repo: string;
    surface: ReleaseSurface;
    platform: Platform;
    count: number;
  }[];
}

export interface IngestResult {
  repo: string;
  fetched: number;
  error?: string;
}

export type ItemCategory = "feature" | "enhancement" | "bugfix" | "maintenance";

export interface ReleaseItem {
  id: string;
  release_id: string;
  ord: number;
  category: ItemCategory;
  text: string;
  parity_notes: string | null;
  linked_flag_ids: string[];
  linear_ticket_ids: string[];
  /** Manual GA/EA label override. null = auto-derive from linked_flag_ids. */
  rollout_override: "ga" | "ea" | null;
  created_at: string;
}

/** One github_release wrapped by a curated release. Populated from the
 *  join in list/feed responses so the client doesn't need a second call. */
export interface ReleaseBuild {
  github_release_id: number;
  repo: string;
  surface: ReleaseSurface;
  platform: Platform;
  tag_name: string;
  name: string | null;
  body: string | null;
  html_url: string | null;
  author_login: string | null;
  published_at: string;
}

export interface Release {
  id: string;
  github_release_ids: number[];
  status: PublishStatus;
  title: string | null;
  narrative: string | null;
  media_urls: string[];
  /** Per-image caption, keyed by the media_urls entry it annotates. A URL
   *  absent from the map has no caption. */
  media_captions: Record<string, string>;
  release_date_override: string | null;
  /** Optional editor-supplied body used as LLM input in place of the
   *  concatenated github release bodies. null means "use github bodies". */
  source_body_override: string | null;
  created_at: string;
  published_at: string | null;
  created_by_email: string | null;
  published_by_email: string | null;
  /** Editor provenance. See FeatureEntry.last_edited_*. */
  last_edited_by: string | null;
  last_edited_at: string | null;
  /** Timeline prominence override. null = auto (client infers 'minor' when
   *  every item is a bugfix/maintenance category). Explicit 'minor' /
   *  'normal' forces the label regardless of item mix. */
  visual_prominence: "minor" | "normal" | null;
  /** Editor hid this curated release from the Timeline. Stays visible to
   *  editors (with an Unhide affordance); dropped from the feed for everyone
   *  else. Non-destructive — the row and its items are untouched. */
  hidden: boolean;
  /** Set to a timestamp while an async LLM seed (narrative + items) runs
   *  right after Convert; null once it finishes (or fails). The client polls
   *  the release until this clears and shows a "Generating…" indicator. */
  seeding_at: string | null;
  /** Declared surface of a PLANNED release (one scheduled before any GitHub
   *  build exists — github_release_ids is empty). null once builds exist /
   *  for ordinary converted releases. */
  planned_surface: "mobile" | "web" | null;
  /** Optional build-cut day (YYYY-MM-DD) for mobile releases. Mirrors to the
   *  release calendar as its own all-day "Build Cut EOD" event. Defaults to
   *  the Wednesday before the target date when scheduling a mobile release. */
  build_cut_date: string | null;
  // Populated by /api/releases + /api/feed. One entry per wrapped github_release
  // (usually 1 for web, 2 for mobile with an iOS + Android pair).
  builds?: ReleaseBuild[];
  items?: ReleaseItem[];
}

// ─── Feed ────────────────────────────────────────────────────────────

export type FeedItem =
  | { type: "release"; sort_date: string; data: Release }
  | {
      type: "feature_announcement";
      sort_date: string;
      data: EnrichedFeature;
    }
  | { type: "release_rollup"; sort_date: string; data: ReleaseRollup };

/** Auto-surfaced GitHub releases since a cutoff that nobody has curated
 *  yet — collapsed into ONE Timeline entry so raw build noise (nightlies,
 *  patches) never floods it. Older-than-cutoff releases are omitted. */
export interface ReleaseRollup {
  count: number;
  /** ISO cutoff date — releases before this aren't surfaced. */
  since: string;
  builds: ReleaseBuild[];
}

// ─── Edit history ────────────────────────────────────────────────────

/** One row per changed field on an entity. `before` / `after` are the raw
 *  values (as JSON) — clients render them with the same helpers used in the
 *  editor UI. */
export interface EditHistoryChange {
  field: string;
  before: unknown;
  after: unknown;
}

export type EditHistoryEntityType =
  | "feature_entries"
  | "releases"
  | "release_items"
  | "llm_prompts";

export type EditHistoryAction =
  | "update"
  | "publish"
  | "unpublish"
  | "create"
  | "delete";

export interface EditHistoryEntry {
  id: string;
  entity_type: EditHistoryEntityType | string;
  entity_id: string;
  actor_email: string | null;
  action: EditHistoryAction | string;
  changes: EditHistoryChange[];
  created_at: string;
}

// ─── Prompts ─────────────────────────────────────────────────────────

export type PromptKey = "release_narrative" | "release_items";

export interface PromptRow {
  key: PromptKey;
  content: string;
  updated_at: string | null;
  updated_by: string | null;
  is_default: boolean;
}
